import type { QueryCache } from '@tanstack/react-query';

export interface LoadedAtStore {
  get(): number | undefined;
  /** The time a restored cache was last loaded from Core. */
  seed(time: number | undefined): void;
  subscribe(listener: () => void): () => void;
}

const stores = new WeakMap<QueryCache, LoadedAtStore>();

/**
 * When a request last succeeded for this cache. `dataUpdatedAt` also moves when the app writes
 * the cache itself (`setQueryData`, as an optimistic update does), so it cannot say when
 * anything was loaded from Core. Created before the first request, at the app's root.
 */
export function loadedAtStore(cache: QueryCache): LoadedAtStore {
  const existing = stores.get(cache);
  if (existing) return existing;
  let loadedAt: number | undefined;
  const listeners = new Set<() => void>();
  cache.subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'success' && !event.action.manual) {
      loadedAt = event.query.state.dataUpdatedAt;
    } else if (event.type === 'removed' && cache.getAll().length === 0) {
      loadedAt = undefined;
    } else {
      return;
    }
    for (const listener of listeners) listener();
  });
  const store: LoadedAtStore = {
    get: () => loadedAt,
    seed(time) {
      if (typeof time !== 'number') return;
      loadedAt = time;
      for (const listener of listeners) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
  stores.set(cache, store);
  return store;
}
